import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  getBranchFiles,
  getLogs,
  selectBranchFiles,
  selectIsLoadingLogs,
  selectLogs,
  setBranchFiles, setIsLoadingLogs, setLogs, setLogsChecked
} from './redux';
import { toast } from 'utils/toast';
import { Branch, selectHotelData, setHotelData } from 'pages/redux';
import {
  LogGroups, LogKey, LogType, logGroupsRaw
} from './Logs';
import { selectPermission, selectToken } from 'redux/global';
import { canViewAllAudits } from 'pages/Settings/redux';
import { isReportContextStandingLogType } from 'pages/Reporting/reportContextUsers';
import { usePath } from 'pages/hooks';
import { useLocation } from 'react-router-dom';
import { syncAppDbCache } from 'utils/syncAppDbCache';
import { store } from 'redux/store';
import {
  buildInventoryGroupAudits,
  buildInventoryItemAudits,
  departmentsPresentInLogs,
  shortenBranchFileDetails,
} from './logUtils';

export type Log = {
  id: string,
  type: string,
  message: string,
  date: string,
  value: string,
  updatedAsOf: string,
  isDelete?: string,
  /** Snapshot of the deleted log on 'Audit deleted' rows (restore re-inserts from this). */
  logTable?: Log | null,
  /** Department (branch) this cash event belongs to. */
  department?: string | null,
  /** Inventory category for inventory-related cash / lifecycle events. */
  inventory_type?: string | null,
  /** Units sold or adjusted for inventory item audits. */
  units?: string | number | null,
  /** Inventory item id when the audit (type) is an inventory item. */
  inventory_item_id?: string | null,
  /** Staff id from auth of the user who created this log. */
  created_by?: string | null,
  /** Booking id for stay-scoped audits (e.g. Key card access). */
  booking_id?: string | null,
  /** Full booking snapshot at log time (nested so booking.id cannot overwrite log id). */
  booking?: Record<string, any> | null,
};

export const oneYearAgo = new Date();
oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

const actionsLogTypesForPermission = (
  permission: number | null | undefined,
): LogGroups['Actions'] => (
  canViewAllAudits(permission)
    ? [...logGroupsRaw.Actions]
    : logGroupsRaw.Actions.filter((type) => !isReportContextStandingLogType(type))
);

export const useLogs = () => {
  const logs = useAppSelector(selectLogs);
  const isLoadingLogs = useAppSelector(selectIsLoadingLogs);
  const branchFiles = useAppSelector(selectBranchFiles);
  const hotelData = useAppSelector(selectHotelData);
  const permission = useAppSelector(selectPermission);
  const { branches } = hotelData || {};
  const logGroups: LogGroups = {
    ...logGroupsRaw,
    Hospitality: [...logGroupsRaw.Hospitality],
    Departments: [...logGroupsRaw.Departments],
    'Department entries': [...logGroupsRaw['Department entries']],
    Inventory: [...logGroupsRaw.Inventory],
    'Inventory Group': [...logGroupsRaw['Inventory Group']],
    Actions: actionsLogTypesForPermission(permission),
  };

  const activeBranches = (branches || []).filter((b) => !b.isDeleted);
  const deletedBranches = (branches || []).filter((b) => b.isDeleted);
  const allBranchNames = (branches || []).map((b) => b.name);
  const departmentsInLogs = new Set(departmentsPresentInLogs(logs, allBranchNames));

  // Deleted departments only appear in audits while they still have logs
  // (via department field or legacy type = department name).
  const deletedWithLogs = deletedBranches
    .map((b) => b.name)
    .filter((name) => departmentsInLogs.has(name));

  const deletedDepartmentNames = new Set(deletedWithLogs);
  logGroups.Departments = [
    ...activeBranches.map((b) => b.name),
    ...deletedWithLogs,
    ...logGroups.Departments,
  ];
  logGroups['Inventory Group'] = buildInventoryGroupAudits({
    inventoryTypes: hotelData?.prefs?.inventoryTypes,
    inventory: hotelData?.inventory,
    logs,
  });
  logGroups.Inventory = buildInventoryItemAudits({
    inventory: hotelData?.inventory,
    logs,
    inventoryGroupNames: logGroups['Inventory Group'],
  });

  const activeInventoryItemNames = new Set(
    (hotelData?.inventory || [])
      .map((item) => item?.name?.trim().toLowerCase())
      .filter(Boolean) as string[],
  );
  const activeInventoryGroupNames = new Set<string>();
  (hotelData?.prefs?.inventoryTypes || []).forEach((t) => {
    if (t?.trim()) activeInventoryGroupNames.add(t.trim().toLowerCase());
  });
  (hotelData?.inventory || []).forEach((item) => {
    if (item?.type?.trim()) activeInventoryGroupNames.add(item.type.trim().toLowerCase());
  });

  // Renamed/removed inventory audits stay visible while matching logs still exist.
  const deletedInventoryItemNames = new Set(
    logGroups.Inventory.filter((name) => !activeInventoryItemNames.has(name.toLowerCase())),
  );
  const deletedInventoryGroupNames = new Set(
    logGroups['Inventory Group'].filter(
      (name) => !activeInventoryGroupNames.has(name.toLowerCase()),
    ),
  );

  const dispatch = useAppDispatch();

  const getLogsFromBranchFiles = () => {
    const addLogs: Log[] = [];

    logGroups.Departments?.forEach((b) => {
      if (branchFiles?.[b]) {
        Object.entries(branchFiles[b]).forEach(([sheetName, sheet]) => {
          sheet.filter((s) => +new Date(s.date) >= +oneYearAgo).forEach((l) => {
            const fileName = l.sheetName || sheetName;
            const newLog: Log = {
              id: `F_${b}_${fileName}_${l.id}`,
              type: 'Document',
              message: `!${b}! imported a document file &${shortenBranchFileDetails(l.message)}& in &${fileName}& by |${l.updatedBy}|`,
              date: l.date,
              value: l.value.toString(),
              updatedAsOf: l.updatedAsOf,
              department: b,
            };
            addLogs.push(newLog);
          });
        });
      }
    });

    return [...addLogs];
  };

  const fetchLogs = async () => {
    const res = await dispatch(getLogs());

    if (res.status === 'success') {
      const branchFileLogs = getLogsFromBranchFiles();
      dispatch(setLogs([...res.data, ...branchFileLogs]));
      syncAppDbCache();
      return;
    }
    toast(res.data, { type: 'error' });
  };

  const fetchBranchFiles = async (noToast?: boolean) => {
    const res = await dispatch(getBranchFiles());

    if (res.status === 'success') {
      dispatch(setBranchFiles(res.data.branchFiles));
      return;
    }

    if (!noToast) {
      toast(res.data, { type: 'error' });
    }
  };

  const deleteLocalLog = (id: string | number) => {
    // Read from the store so a concurrent get_added_log (e.g. "Audit deleted")
    // is not wiped by filtering a stale hook snapshot.
    const { logs: currentLogs } = store.getState().logs;
    const newLogs = (currentLogs || []).filter((l) => l.id.toString() !== id.toString());
    dispatch(setLogs(newLogs));
    syncAppDbCache();
  };

  const deleteLocalLogType = (type: string) => {
    const { logs: currentLogs } = store.getState().logs;
    const newLogs = (currentLogs || []).filter((l) => l.type !== type);
    dispatch(setLogs([...newLogs]));
    syncAppDbCache();
  };

  const updateLocalLog = (log: Log) => {
    const { logs: currentLogs } = store.getState().logs;
    const newLogs = [...(currentLogs || []).filter((l: Log) => l.id.toString() !== log.id.toString()),
      log];
    dispatch(setLogs([...newLogs]));
    syncAppDbCache();
  };

  const updateLocalBranch = (branch: Branch, previousName?: string) => {
    const removeNames = new Set(
      [branch?.name, previousName].filter(Boolean) as string[],
    );
    const newBranches = [
      ...(branches || []).filter((b) => !removeNames.has(b.name)),
      branch,
    ];
    if (hotelData) {
      dispatch(setHotelData({ ...hotelData, branches: newBranches }));
      syncAppDbCache();
    }
  };

  const deleteLocalBranch = (branch: string) => {
    const existing = (branches || []).find((b) => b.name === branch);
    if (!existing || !hotelData) return;
    const newBranches = (branches || []).map((b) =>
      (b.name === branch ? { ...b, staff: [], isDeleted: true } : b));
    dispatch(setHotelData({ ...hotelData, branches: newBranches }));
    syncAppDbCache();
  };

  const deleteLocalBranchFile = (branch: string, sheetName: string) => {
    const newBranchFiles = { ...branchFiles?.[branch] || {} };
    delete newBranchFiles[sheetName];
    dispatch(setBranchFiles({ ...branchFiles, [branch]: newBranchFiles }));
  };

  const deleteLocalBranchFiles = (branch: string) => {
    const newBranchFiles = { ...branchFiles };
    delete newBranchFiles[branch];
    dispatch(setBranchFiles({ ...newBranchFiles }));
  };

  return {
    isLoadingLogs,
    logs,
    logGroups,
    deletedDepartmentNames,
    deletedInventoryItemNames,
    deletedInventoryGroupNames,
    deleteLocalLog,
    deleteLocalLogType,
    updateLocalLog,
    getLogsFromBranchFiles,
    fetchBranchFiles,
    fetchLogs,
    deleteLocalBranchFile,
    deleteLocalBranchFiles,
    updateLocalBranch,
    deleteLocalBranch
  };
};

export const useFetchLogs = () => {
  const logs = useAppSelector(selectLogs);
  const branchFiles = useAppSelector(selectBranchFiles);
  const hotelData = useAppSelector(selectHotelData);
  const token = useAppSelector(selectToken);
  const permission = useAppSelector(selectPermission);
  const { isHomePath } = usePath();
  const { branches } = hotelData || {};
  const logGroups: LogGroups = {
    ...logGroupsRaw,
    Hospitality: [...logGroupsRaw.Hospitality],
    Departments: [...logGroupsRaw.Departments],
    'Department entries': [...logGroupsRaw['Department entries']],
    Inventory: [...logGroupsRaw.Inventory],
    'Inventory Group': [...logGroupsRaw['Inventory Group']],
    Actions: actionsLogTypesForPermission(permission),
  };
  const activeBranches = (branches || []).filter((b) => !b.isDeleted);
  const allBranchNames = (branches || []).map((b) => b.name);
  const departmentsInLogs = new Set(departmentsPresentInLogs(logs, allBranchNames));
  const deletedWithLogs = (branches || [])
    .filter((b) => b.isDeleted)
    .map((b) => b.name)
    .filter((name) => departmentsInLogs.has(name));
  logGroups.Departments = [
    ...activeBranches.map((b) => b.name),
    ...deletedWithLogs,
    ...logGroups.Departments,
  ];
  logGroups['Inventory Group'] = buildInventoryGroupAudits({
    inventoryTypes: hotelData?.prefs?.inventoryTypes,
    inventory: hotelData?.inventory,
    logs,
  });
  logGroups.Inventory = buildInventoryItemAudits({
    inventory: hotelData?.inventory,
    logs,
    inventoryGroupNames: logGroups['Inventory Group'],
  });
  const dispatch = useAppDispatch();
  const {
    fetchBranchFiles,
    getLogsFromBranchFiles,
    fetchLogs
  } = useLogs();
  const location = useLocation();
  const logsRef = useRef(logs);

  useEffect(() => {
    // Do not clear isLoadingLogs here — Navigation's metric aggregation owns
    // that flag so /metrics waits until buckets are ready (avoids tab freeze).
    logsRef.current = logs;
  }, [logs]);

  useEffect(() => {
    if (!branchFiles || !logsRef.current) return;
    const baseLogs = (logsRef.current || []).filter(
      (l) => !l.id.toString().includes('F_'),
    );
    const logsFromBranchFiles = getLogsFromBranchFiles();
    const prevFileCount = (logsRef.current || []).filter(
      (l) => l.id.toString().includes('F_'),
    ).length;
    // Skip no-op merges so metrics/logs pages do not remount for free.
    if (prevFileCount === logsFromBranchFiles.length && logsFromBranchFiles.length === 0) {
      return;
    }
    dispatch(setLogs([...baseLogs, ...logsFromBranchFiles]));
  }, [branchFiles]);

  useEffect(() => {
    if (!token || !hotelData?.id || isHomePath) return;

    if (!logs) {
      dispatch(setIsLoadingLogs(true));
      (async () => {
        await fetchLogs();
      })();
    }

    if (branchFiles == null) {
      (async () => {
        await fetchBranchFiles();
      })();
    }
  }, [hotelData, location, logs, token, branchFiles, isHomePath]);
};

export const useLogsSelector = ({
  triggerAllItems0,
  triggerAllItems1,
}:{
  triggerAllItems0?: Function,
  triggerAllItems1?: Function,
}) => {
  const dispatch = useAppDispatch();
  const { logGroups } = useLogs();

  const onClickShowAll = (checked1: string[]) => {
    if (checked1.includes('Show all')) {
      dispatch(setLogsChecked([...[]]));
    } else {
      let allItems: string[] = ['Show all'];
      Object.entries(logGroups).forEach(([k, v]) => {
        const allItemsOfThisGroup = [...v, k];
        allItems = [...allItems, ...allItemsOfThisGroup];
      });
      dispatch(setLogsChecked([...allItems]));
      triggerAllItems0?.();
    }
  };

  const onClickAuditKey = (key: LogKey, checked1: string[]) => {
    const value = (logGroups as any)[key] as string[];
    const allChildrenSelected = value.length > 0
      && value.every((t) => checked1.includes(t));
    // Fully selected (group key or every child) → clear group; else select all (incl. from partial).
    if (checked1.includes(key) || allChildrenSelected) {
      const newC = checked1.filter((c) => ![...value, key, 'Show all']
        .includes(c as LogType));
      dispatch(setLogsChecked(newC));
    } else {
      const withoutGroup = checked1.filter(
        (c) => c !== key && c !== 'Show all' && !value.includes(c as LogType),
      );
      dispatch(setLogsChecked([...withoutGroup, ...value, key]));
      triggerAllItems0?.();
    }
  };

  const onClickAudit = (key: LogKey, logType: string, checked1: string[]) => {
    if (checked1.includes(logType)) {
      const newC = checked1.filter((c) => c !== 'Show all' && c !== key && c !== logType);
      dispatch(setLogsChecked(newC));
    } else {
      const newC = [...checked1, logType];
      dispatch(setLogsChecked(newC));
      triggerAllItems1?.(key, logType);
    }
  };
  return {
    onClickShowAll,
    onClickAuditKey,
    onClickAudit,
  };
};

export const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState<Record<string, undefined | number>>({
    width: 278,
    height: undefined,
  });
  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return windowSize;
};
